import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ShowChart as ChartIcon,
  Build as BuildIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Line } from 'react-chartjs-2';

const CarRaceHistory = ({ 
  car, 
  raceHistory, 
  loadingRaceHistory, 
  showRaceHistory, 
  setShowRaceHistory,
  chartData,
  chartOptions,
  setCreateTaskModalOpen,
  setCheckupTaskMode
}) => {
  const { t } = useTranslation('cars');

  return (
    <>
      {/* Race History Chart */}
      <Paper 
        elevation={3} 
        sx={{ p: 2, mb: 3 }}
      >
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center" 
          onClick={() => setShowRaceHistory(!showRaceHistory)}
          sx={{ cursor: 'pointer', mb: 1 }}
        >
          <Box display="flex" alignItems="center">
            <ChartIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">
              {t('carDetail.raceHistory')}
            </Typography>
          </Box>
          <IconButton>
            {showRaceHistory ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        
        <Collapse in={showRaceHistory}>
          {loadingRaceHistory ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : raceHistory.length > 0 ? (
            <>
              <Box sx={{ height: 300, mb: 3 }}>
                <Line data={chartData} options={chartOptions} />
              </Box>
              
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('carDetail.event')}</TableCell>
                      <TableCell align="right">{t('carDetail.eventDates')}</TableCell>
                      <TableCell align="right">{t('carDetail.laps')}</TableCell>
                      <TableCell align="right">{t('carDetail.kilometers')}</TableCell>
                      <TableCell align="right">{t('carDetail.cumulativeKilometers')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {raceHistory.map((race) => (
                      <TableRow key={race.eventId}>
                        <TableCell component="th" scope="row">
                          {race.title}
                        </TableCell>
                        <TableCell align="right">
                          {format(new Date(race.date), 'dd/MM/yyyy')}
                        </TableCell>
                        <TableCell align="right">{race.laps}</TableCell>
                        <TableCell align="right">{race.kilometers} km</TableCell>
                        <TableCell align="right">{race.cumulativeKilometers} km</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box mt={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('carDetail.totalRaces')}: {raceHistory.length}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('carDetail.totalKilometers')}: {car.totalKilometers || 0} km
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('carDetail.kilometersSinceCheckup')}: {car.totalKilometersSinceCheckup || 0} km
                </Typography>
                
                {car.totalKilometersSinceCheckup > car.checkupThreshold && (
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<BuildIcon />}
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => {
                      setCreateTaskModalOpen(true);
                      setCheckupTaskMode(true);
                    }}
                  >
                    {t('carDetail.newCheckupTask')}
                  </Button>
                )}
              </Box>
            </>
          ) : (
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center" 
              justifyContent="center"
              py={4}
            >
              <Typography variant="body1" color="text.secondary" align="center">
                {t('carDetail.noRaceHistory')}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                {t('carDetail.participateInRaces')}
              </Typography>
            </Box>
          )}
        </Collapse>
      </Paper>
    </>
  );
};

export default CarRaceHistory; 